import React, { useMemo, useState } from "react";
import { Button, Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import InsightsIcon from "@mui/icons-material/Insights";
import { useIsDarkMode } from "../../theme/ThemeContext";
import { PROBLEM_TYPES as WEB_PROBLEM_TYPES } from "../Forms/Misc/OrderProblemForm";
import { EmptyState } from "../../components/UI/States";

/**
 * Analytics tab for the Order Problems dashboard. The dashboard already loads
 * every problem (minus image/note payloads), so everything here is computed
 * client-side from that list. Everything is keyed on createdAt and the
 * problem's current status — resolution-timing stats are deliberately left
 * out for now (the list endpoint's `resolvedAt` field is ready when they
 * come back).
 *
 * Problem-type counts mirror ProblemChips on the dashboard: web-form
 * submissions carry a list of type labels in `errors`, driver-app reports a
 * single `problemType` enum.
 */

const RANGES = [
  { key: "30d", label: "Last 30 days", days: 30, bucket: "week" },
  { key: "90d", label: "Last 90 days", days: 90, bucket: "week" },
  { key: "12m", label: "Last 12 months", days: 365, bucket: "month" },
  { key: "all", label: "All time", days: null, bucket: "month" },
];

const DAY_MS = 86400000;

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const monthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const monthLabel = (key) => {
  const [year, month] = key.split("-");
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString([], {
    month: "short",
    year: "2-digit",
  });
};

// Whole days open, floored at 0 for clock-skewed records.
const daysBetween = (from, to) => Math.max(0, (to - from) / DAY_MS);

const formatDays = (days) => {
  if (days === null) return "—";
  if (days < 1) return `${Math.max(1, Math.round(days * 24))}h`;
  return `${Math.round(days * 10) / 10}d`;
};

// Same source rule as ProblemChips: `errors` means the web form (plus the
// explicit source flag newer web docs carry); everything else is the driver app.
const isWebForm = (problem) =>
  problem.source === "web_form" || (problem.errors || []).length > 0;

// The web form lets people type their own problem types alongside the fixed
// checkboxes, so raw labels are full of one-off wordings of the same idea
// ("No phone number", "phone numbers", "No contact information..."). For the
// type breakdown, fold write-ins into themed groups — first match wins — and
// leave the fixed checkbox types (and the driver-app enum labels) untouched.
const WRITE_IN_GROUPS = [
  { label: "Job Not Ready", match: /not\s+ready/i },
  { label: "Job Not Found", match: /not\s+found|can'?t\s+find|cannot\s+find/i },
  { label: "Damaged Product", match: /damag/i },
  {
    label: "Wrong Product Delivered",
    match: /wrong\s+(product|material|item)/i,
  },
  {
    label: "Missing Product",
    match: /missing\s+(product|material|item)|shorted/i,
  },
  { label: "Missing / wrong contact info", match: /phone|contact/i },
  {
    label: "Address / delivery info",
    match: /address|gate\s*code|delivery\s+info|journey|directions/i,
  },
  { label: "Payment issue", match: /not\s+paid|payment|c\.?o\.?d\b/i },
];

const OTHER_WRITE_INS = "Other write-ins";

const canonicalTypeLabel = (rawLabel) => {
  if (WEB_PROBLEM_TYPES.includes(rawLabel)) return rawLabel;
  const group = WRITE_IN_GROUPS.find((g) => g.match.test(rawLabel));
  return group ? group.label : OTHER_WRITE_INS;
};

const typeLabelsFor = (problem, problemTypeLabel) =>
  (problem.errors || []).length
    ? problem.errors
    : [problemTypeLabel(problem.problemType)];

/**
 * Segmented control (iOS-style) for small exclusive choices — the
 * Problems/Analytics switch, the status filter, and the time range here.
 * Same look as the themed MUI ToggleButtonGroup.
 */
export const Segmented = ({ options, value, onChange, label, className = "" }) => (
  <div
    role="group"
    aria-label={label}
    className={`inline-flex max-w-full gap-0.5 overflow-x-auto rounded-xl bg-[color:var(--app-hover)] p-[3px] ${className}`}
  >
    {options.map((option) => {
      const active = option.key === value;
      return (
        <button
          key={option.key}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(option.key)}
          className={`min-h-[2.25rem] shrink-0 whitespace-nowrap rounded-[9px] px-3 py-1.5 text-sm font-semibold transition ${
            active
              ? "bg-white text-[color:var(--app-text)] shadow-sm dark:bg-white/15 dark:shadow-none"
              : "app-muted hover:text-[color:var(--app-text)]"
          }`}
        >
          {option.label}
          {option.count !== undefined && (
            <span className="ml-1.5 text-xs font-medium tabular-nums opacity-70">
              {option.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

const Kpi = ({ label, value, hint, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="app-card flex min-w-0 flex-col rounded-2xl border p-4 text-left transition hover:-translate-y-0.5 hover:border-sky-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
  >
    <span className="app-muted text-xs font-medium">{label}</span>
    <span className="mt-1 text-2xl font-bold tabular-nums tracking-tight">
      {value}
    </span>
    {hint && <span className="app-muted mt-0.5 text-xs">{hint}</span>}
  </button>
);

const BreakdownBar = ({
  label,
  count,
  total,
  variants,
  color = "bg-sky-500",
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-[color:var(--app-hover)]"
    title={
      variants?.length ? `Includes write-ins:\n${variants.join("\n")}` : label
    }
  >
    <span className="w-40 shrink-0 truncate sm:w-60">
      {label}
      {variants?.length > 0 && (
        <span className="app-muted ml-1 text-xs">({variants.length})</span>
      )}
    </span>
    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-500/15">
      <div
        className={`h-full rounded-full ${color}`}
        style={{ width: `${total ? Math.max(2, (count / total) * 100) : 0}%` }}
      />
    </div>
    <span className="w-16 shrink-0 text-right tabular-nums">
      <span className="font-semibold">{count.toLocaleString()}</span>
      <span className="app-muted ml-1 text-xs">
        {total ? Math.round((count / total) * 100) : 0}%
      </span>
    </span>
  </button>
);

const SectionCard = ({ title, hint, children }) => (
  <section className="app-card rounded-2xl border p-5">
    <h3 className="font-semibold">{title}</h3>
    {hint && <p className="app-muted mb-4 mt-0.5 text-xs">{hint}</p>}
    <div className={hint ? "" : "mt-4"}>{children}</div>
  </section>
);

const OrderProblemsAnalytics = ({
  problems,
  problemTypeLabel,
  onOpenProblem,
}) => {
  const isDark = useIsDarkMode();
  const [rangeKey, setRangeKey] = useState("90d");
  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[1];
  // Any stat can drill down to its submissions: { title, problems }.
  const [drill, setDrill] = useState(null);

  const stats = useMemo(() => {
    const all = problems || [];
    const now = new Date();
    const rangeStart = range.days
      ? new Date(now.getTime() - range.days * DAY_MS)
      : null;
    const inWindow = (date) => date && (!rangeStart || date >= rangeStart);

    const reported = all.filter((p) => inWindow(parseDate(p.createdAt)));
    const webCount = reported.filter(isWebForm).length;

    const openNow = all.filter((p) => p.status === "open");
    const oldestOpen = openNow.reduce((oldest, p) => {
      const created = parseDate(p.createdAt);
      if (!created) return oldest;
      return !oldest || created < oldest ? created : oldest;
    }, null);

    // Status-based only — resolution timing is deliberately left out for now,
    // so "Resolved" is the share of problems reported in the range that are
    // resolved today. Lists (not just counts) are kept so every stat can
    // drill down to its submissions.
    const resolvedList = reported.filter((p) => p.status === "resolved");
    const photosList = reported.filter((p) => (p.imageCount || 0) > 0);

    // Trend buckets: 7-day buckets ending today for day ranges, calendar
    // months otherwise ("all time" starts at the earliest record).
    const buckets = [];
    if (range.bucket === "week") {
      const weeks = Math.ceil(range.days / 7);
      for (let i = weeks - 1; i >= 0; i--) {
        // Clamp the oldest (possibly partial) week to the selected range so
        // the chart totals match the KPI counts above it.
        const start = new Date(
          Math.max(now.getTime() - (i + 1) * 7 * DAY_MS, rangeStart.getTime()),
        );
        buckets.push({
          start,
          end: new Date(now.getTime() - i * 7 * DAY_MS),
          label: start.toLocaleDateString([], {
            month: "short",
            day: "numeric",
          }),
          open: 0,
          resolved: 0,
        });
      }
    } else {
      let firstMonth = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      if (!range.days) {
        const earliest = all.reduce((min, p) => {
          const created = parseDate(p.createdAt);
          return created && (!min || created < min) ? created : min;
        }, null);
        if (earliest)
          firstMonth = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
      }
      const cursor = new Date(firstMonth);
      while (cursor <= now) {
        const start = new Date(cursor);
        buckets.push({
          start,
          end: new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1),
          label: monthLabel(monthKey(start)),
          open: 0,
          resolved: 0,
        });
        cursor.setMonth(cursor.getMonth() + 1);
      }
    }
    const bucketFor = (date) =>
      date && buckets.find((b) => date >= b.start && date < b.end);
    all.forEach((p) => {
      const bucket = bucketFor(parseDate(p.createdAt));
      if (!bucket) return;
      if (p.status === "resolved") bucket.resolved++;
      else bucket.open++;
    });

    // Problem type / branch breakdowns over the problems reported in range.
    // Web-form `errors` entries are grouped via canonicalTypeLabel and deduped
    // per report, so one report writing the same idea two ways counts once;
    // driver-app enum labels pass through as-is.
    const byType = new Map(); // label -> { count, variants: Set, problems: [] }
    const byBranch = new Map(); // label -> { count, problems: [] }
    reported.forEach((p) => {
      const fromErrors = (p.errors || []).length > 0;
      const raws = fromErrors ? p.errors : [problemTypeLabel(p.problemType)];
      const groups = new Map(); // canonical label -> raw write-ins in this report
      raws.forEach((raw) => {
        const label = fromErrors ? canonicalTypeLabel(raw) : raw;
        if (!groups.has(label)) groups.set(label, []);
        if (raw !== label) groups.get(label).push(raw);
      });
      groups.forEach((rawVariants, label) => {
        const entry = byType.get(label) || {
          count: 0,
          variants: new Set(),
          problems: [],
        };
        entry.count += 1;
        entry.problems.push(p);
        rawVariants.forEach((raw) => entry.variants.add(raw));
        byType.set(label, entry);
      });
      const branch = (p.branch || "").trim() || "Unknown";
      const branchEntry = byBranch.get(branch) || { count: 0, problems: [] };
      branchEntry.count += 1;
      branchEntry.problems.push(p);
      byBranch.set(branch, branchEntry);
    });
    const types = [...byType.entries()]
      .map(([label, { count, variants, problems: list }]) => ({
        label,
        count,
        variants: [...variants],
        problems: list,
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    const branchesSorted = [...byBranch.entries()]
      .map(([label, { count, problems: list }]) => ({
        label,
        count,
        problems: list,
      }))
      .sort((a, b) => b.count - a.count);

    // Repeat customers. Web-form reports are anonymous and store no customer,
    // so this only reflects driver-app reports.
    const byCustomer = new Map();
    reported.forEach((p) => {
      const name = (p.customer || "").trim();
      if (!name) return;
      const entry = byCustomer.get(name.toLowerCase()) || {
        name,
        total: 0,
        open: 0,
        problems: [],
      };
      entry.total++;
      entry.problems.push(p);
      if (p.status === "open") entry.open++;
      byCustomer.set(name.toLowerCase(), entry);
    });
    const topCustomers = [...byCustomer.values()]
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);

    const longestOpen = openNow
      .map((p) => ({ problem: p, created: parseDate(p.createdAt) }))
      .filter((entry) => entry.created)
      .sort((a, b) => a.created - b.created)
      .slice(0, 8);

    return {
      reported: reported.length,
      reportedList: reported,
      webCount,
      openNow: openNow.length,
      openList: openNow,
      oldestOpen,
      resolvedCount: resolvedList.length,
      resolvedList,
      withPhotos: photosList.length,
      photosList,
      buckets,
      types,
      branches: branchesSorted,
      topCustomers,
      longestOpen,
      now,
    };
  }, [problems, range, problemTypeLabel]);

  if ((problems || []).length === 0) {
    return (
      <div className="app-card rounded-2xl border">
        <EmptyState
          icon={InsightsIcon}
          title="No analytics yet"
          description="No order problems have been reported yet — analytics will appear once reports come in."
        />
      </div>
    );
  }

  const rangeHint = range.days ? range.label.toLowerCase() : "all time";

  return (
    <div className="space-y-6">
      {/* Time range */}
      <Segmented
        label="Time range"
        options={RANGES}
        value={rangeKey}
        onChange={setRangeKey}
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="Reported"
          value={stats.reported.toLocaleString()}
          hint={`${stats.reported - stats.webCount} from the driver app · ${
            stats.webCount
          } from the website form`}
          onClick={() =>
            setDrill({
              title: `Reported — ${range.label}`,
              problems: stats.reportedList,
            })
          }
        />
        <Kpi
          label="Open now"
          value={stats.openNow.toLocaleString()}
          hint={
            stats.oldestOpen
              ? `Oldest has been open ${formatDays(
                  daysBetween(stats.oldestOpen, stats.now),
                )}`
              : "All problems, regardless of time range."
          }
          onClick={() =>
            setDrill({ title: "Open now — all time", problems: stats.openList })
          }
        />
        <Kpi
          label="Resolved"
          value={stats.resolvedCount.toLocaleString()}
          hint={`${
            stats.reported
              ? Math.round((stats.resolvedCount / stats.reported) * 100)
              : 0
          }% of problems reported in the ${rangeHint}.`}
          onClick={() =>
            setDrill({
              title: `Resolved — ${range.label}`,
              problems: stats.resolvedList,
            })
          }
        />
        <Kpi
          label="With photos"
          value={`${
            stats.reported
              ? Math.round((stats.withPhotos / stats.reported) * 100)
              : 0
          }%`}
          hint={`${stats.withPhotos} of ${stats.reported} reports attached at least one photo.`}
          onClick={() =>
            setDrill({
              title: `With photos — ${range.label}`,
              problems: stats.photosList,
            })
          }
        />
      </div>

      {/* Reporting trend */}
      <SectionCard
        title={`Reported per ${range.bucket}`}
        hint="Each bar is the problems reported that period, split by whether they're resolved today."
      >
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={stats.buckets}
              margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={isDark ? "#33415566" : "#e2e8f0"}
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: isDark ? "#33415544" : "#f1f5f9" }}
                // Same floating material as menus/popovers in every theme.
                contentStyle={{
                  backgroundColor: "var(--app-popover-bg)",
                  border: "1px solid var(--app-popover-border)",
                  borderRadius: 12,
                  boxShadow: "var(--app-popover-shadow)",
                  WebkitBackdropFilter: "var(--app-glass-filter)",
                  backdropFilter: "var(--app-glass-filter)",
                  color: "var(--app-text)",
                  fontSize: 12,
                }}
                labelStyle={{ color: "var(--app-text)", fontWeight: 600 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="resolved"
                name="Resolved"
                stackId="reported"
                fill="#10b981"
              />
              <Bar
                dataKey="open"
                name="Still open"
                stackId="reported"
                fill="#ef4444"
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* Problem types */}
        <SectionCard
          title="By problem type"
          hint={`Problems reported in the ${rangeHint}; a report can carry several types. Write-ins are grouped by theme (hover a row for the exact wording) — click a type to see its submissions.`}
        >
          <div className="space-y-2.5">
            {stats.types.map((type) => (
              <BreakdownBar
                key={type.label}
                label={type.label}
                count={type.count}
                total={stats.reported}
                variants={type.variants}
                onClick={() =>
                  setDrill({
                    title: `${type.label} — ${range.label}`,
                    problems: type.problems,
                  })
                }
              />
            ))}
          </div>
        </SectionCard>

        {/* Branches */}
        <SectionCard
          title="By branch"
          hint={`Problems reported in the ${rangeHint} — click a branch to see its submissions.`}
        >
          <div className="space-y-2.5">
            {stats.branches.map((branch) => (
              <BreakdownBar
                key={branch.label}
                label={branch.label}
                count={branch.count}
                total={stats.reported}
                color="bg-indigo-500"
                onClick={() =>
                  setDrill({
                    title: `${branch.label} — ${range.label}`,
                    problems: branch.problems,
                  })
                }
              />
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Drill-down: the submissions behind whichever stat was clicked. */}
      <Dialog
        open={!!drill}
        onClose={() => setDrill(null)}
        fullWidth
        maxWidth="md"
      >
        {drill && (
          <div className="flex max-h-[80vh] flex-col">
            <div className="flex items-center justify-between gap-4 border-b app-divider px-5 py-3">
              <p className="min-w-0 truncate font-semibold">
                {drill.title}
                <span className="app-muted ml-2 text-sm font-normal tabular-nums">
                  {drill.problems.length} problem
                  {drill.problems.length === 1 ? "" : "s"}
                </span>
              </p>
              <Button
                size="small"
                startIcon={<CloseIcon />}
                onClick={() => setDrill(null)}
              >
                Close
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="overflow-x-auto">
                <table className="app-table">
                  <thead>
                    <tr className="uppercase tracking-wide">
                      <th className="!px-4">Reported</th>
                      <th className="!px-4">Order #</th>
                      <th className="!px-4">Customer</th>
                      <th className="!px-4">Branch</th>
                      <th className="!px-4">Problem</th>
                      <th className="!px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drill.problems.map((p) => (
                      <tr
                        key={p._id}
                        onClick={() => {
                          setDrill(null);
                          onOpenProblem?.(p._id);
                        }}
                        className="cursor-pointer"
                      >
                        <td className="whitespace-nowrap !px-4 tabular-nums">
                          {p.createdAt
                            ? new Date(p.createdAt).toLocaleDateString()
                            : "—"}
                        </td>
                        <td className="whitespace-nowrap !px-4 font-medium tabular-nums">
                          {p.orderNumber || "—"}
                        </td>
                        <td className="max-w-[180px] truncate !px-4">
                          {p.customer || "—"}
                        </td>
                        <td className="whitespace-nowrap !px-4">
                          {p.branch || "—"}
                        </td>
                        <td
                          className="max-w-[240px] truncate !px-4"
                          title={typeLabelsFor(p, problemTypeLabel).join(", ")}
                        >
                          {typeLabelsFor(p, problemTypeLabel).join(", ")}
                        </td>
                        <td className="whitespace-nowrap !px-4">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                              p.status === "resolved"
                                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                                : "bg-rose-500/15 text-rose-700 dark:text-rose-300"
                            }`}
                          >
                            {p.status === "resolved" ? "Resolved" : "Open"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default OrderProblemsAnalytics;
